import { useContext, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';

import { permissions } from '@/app/contexts/permissions';
import { useExternalLinks } from '@/app/hooks/useExternalLinks';
import { IntlContext } from '@/i18n';
import { signOut } from '@/modules/auth/store/actions';
import { getAllCars } from '@/modules/car/store/selectors';
import { getPressure } from '@/modules/common/store/slice';
import { getMyDriver } from '@/modules/driver/store/selectors';
import { getCity } from '@/modules/partner/store/selectors';
import { AppDispatch } from '@/root-store';
import { useBookingActions } from '@/shared/utils/bookingLinksUtils';
import { INavMenuItem, NavMenu } from '@src-common/view/components/NavMenu';

import { CityPressure } from './CityPressure';
import { LanguageMenu } from './LanguageMenu';

interface IProps {
  onNavigate?: (uri: string) => void;
  className?: string;
}

const isMenuItem = (maybeItem: any): maybeItem is INavMenuItem => Boolean(maybeItem);

// Defined at module level: a component created inside the menu memo would get a
// new identity on every recompute and remount on each render.
const CityPressureMenuItem = () => {
  const { language } = useContext(IntlContext);
  const city = useSelector(getCity);
  const pressure = useSelector(getPressure);

  return <CityPressure value={pressure} cityName={city?.localizedName[language]} />;
};

const MainMenu = ({ onNavigate, className }: IProps) => {
  const dispatch = useDispatch<AppDispatch>();
  const intl = useIntl();
  const { region } = useContext(IntlContext);
  const links = useExternalLinks();
  const cars = useSelector(getAllCars);
  const { can } = useContext(permissions);
  const driver = useSelector(getMyDriver);

  const { openModalAction, goToExternalLinkAction } = useBookingActions({ region, driver });

  const menuItems = useMemo<INavMenuItem[]>(
    () =>
      [
        can.viewTodo && {
          title: intl.formatMessage({
            defaultMessage: 'To-Do List',
            id: 'aVUtxs',
            description: 'nav menu item',
          }),
          onClick: () => onNavigate?.('/todo'),
          keepVisibleOnLargeScreen: true,
        },
        can.viewOverview && {
          title: intl.formatMessage({
            defaultMessage: 'Overview',
            id: 'rLIKeT',
            description: 'nav menu item',
          }),
          onClick: () => onNavigate?.(`/overview`),
          keepVisibleOnLargeScreen: true,
        },
        can.viewDrivers && {
          title: intl.formatMessage({
            defaultMessage: 'Chauffeurs',
            id: '8jZiZo',
            description: 'nav menu item',
          }),
          onClick: () => onNavigate?.(`/chauffeurs`),
          keepVisibleOnLargeScreen: true,
        },
        can.viewCars && {
          title: intl.formatMessage({
            defaultMessage: 'Vehicles',
            id: '4zRzj8',
            description: 'nav menu item',
          }),
          onClick: () => onNavigate?.(cars.length === 0 ? '/add-vehicle' : '/vehicles'),
          keepVisibleOnLargeScreen: true,
        },
        can.viewJourneys && {
          title: intl.formatMessage({
            defaultMessage: 'Journeys',
            id: 'R5qHEZ',
            description: 'nav menu item',
          }),
          onClick: () => onNavigate?.(`/journeys`),
          keepVisibleOnLargeScreen: true,
        },
        can.viewBills && {
          title: intl.formatMessage({
            defaultMessage: 'Bills',
            id: '3lmlzx',
            description: 'nav menu item',
          }),
          onClick: () => onNavigate?.(`/bills`),
          keepVisibleOnLargeScreen: true,
        },
        can.viewProfile && {
          title: intl.formatMessage({
            defaultMessage: 'Profile',
            id: 'qkBbAc',
            description: 'nav menu item',
          }),
          onClick: () => onNavigate?.('/profile'),
          keepVisibleOnLargeScreen: true,
        },
        {
          title: intl.formatMessage({
            defaultMessage: 'Questions',
            id: 'Vsu9vZ',
            description: 'nav menu item',
          }),
          subItems: [
            {
              title: intl.formatMessage({
                defaultMessage: 'FAQ',
                id: 'XfJ/7B',
                description: 'nav menu item',
              }),
              externalLink: links.faq,
            },
            {
              title: intl.formatMessage({
                defaultMessage: 'App',
                id: 'MM/sCI',
                description: 'nav menu item',
              }),
              externalLink: links.downloadDriverApp,
            },
            {
              title: intl.formatMessage({
                defaultMessage: 'Book an Appointment',
                id: 'xhfLcg',
                description: 'nav menu item',
              }),
              isDisabled: !can.bookAnAccreditation,
              externalLink: goToExternalLinkAction,
              onClick: openModalAction,
            },
          ],
        },
        can.viewPressure && {
          Component: CityPressureMenuItem,
        },
        {
          title: intl.formatMessage({
            defaultMessage: 'Settings',
            id: 'S6MKBJ',
            description: 'nav menu item',
          }),
          subItems: [
            {
              title: 'Language',
              Component: LanguageMenu,
            },
            {
              title: intl.formatMessage({
                defaultMessage: 'Log out',
                id: '4CIyDw',
                description: 'nav menu item',
              }),
              onClick: () => dispatch(signOut()),
            },
          ],
        },
      ].filter(isMenuItem) as INavMenuItem[],
    [
      can.viewTodo,
      can.viewProfile,
      can.viewOverview,
      can.viewCars,
      can.viewDrivers,
      can.viewBills,
      can.viewJourneys,
      can.bookAnAccreditation,
      intl,
      links.faq,
      links.downloadDriverApp,
      goToExternalLinkAction,
      openModalAction,
      onNavigate,
      cars.length,
      dispatch,
      can.viewPressure,
    ],
  );

  return (
    <NavMenu
      appName={intl.formatMessage({
        defaultMessage: 'For chauffeurs',
        id: 'nNw0W5',
        description: 'App name in main menu',
      })}
      menuItems={menuItems}
      className={className}
    />
  );
};

export { MainMenu };
